import React, { useState } from 'react';
import { Check, X, ArrowRight, Star, Shield, Clock, Home, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';

const PACKAGES = [
  {
    id: 'basic',
    name: 'Basic',
    price: 500,
    originalPrice: 1000,
    color: 'from-[#0EA5E9] to-[#0284C7]',
    borderColor: 'border-[#0EA5E9]',
    tagBg: 'bg-[#0EA5E9]',
    tests: {
      'Blood Sugar': true,
      'Blood Sugar HbA1c': false,
      'CBC / Total Count': true,
      'Lipid Profile': false,
      'Total Cholesterol, Triglycerides': true,
      'Liver Profile': false,
      'Bilirubin': true,
      'Kidney Profile': false,
      'Creatinine': true,
      'Urine Analysis': true,
      'Thyroid Profile Test': false,
      'TSH': true,
      'Vitamin D & B12': false,
      'Iron': false,
      'Calcium': false,
      'ECG': false,
      'Chest X-ray': false,
      'USG Scan Abdomen': false,
      'Cancer Screening - PSA/CA 125': false,
    },
    testCount: 8,
    highlight: false,
  },
  {
    id: 'essential',
    name: 'Essential',
    price: 1000,
    originalPrice: 2000,
    color: 'from-[#10B981] to-[#059669]',
    borderColor: 'border-[#10B981]',
    tagBg: 'bg-[#10B981]',
    tests: {
      'Blood Sugar': true,
      'Blood Sugar HbA1c': false,
      'CBC / Total Count': true,
      'Lipid Profile': true,
      'Total Cholesterol, Triglycerides': false,
      'Liver Profile': true,
      'Bilirubin': false,
      'Kidney Profile': true,
      'Creatinine': false,
      'Urine Analysis': true,
      'Thyroid Profile Test': false,
      'TSH': false,
      'Vitamin D & B12': false,
      'Iron': false,
      'Calcium': true,
      'ECG': false,
      'Chest X-ray': false,
      'USG Scan Abdomen': false,
      'Cancer Screening - PSA/CA 125': false,
    },
    testCount: 7,
    highlight: false,
  },
  {
    id: 'essential-plus',
    name: 'Essential Plus',
    price: 2500,
    originalPrice: 4000,
    color: 'from-[#8B5CF6] to-[#7C3AED]',
    borderColor: 'border-[#8B5CF6]',
    tagBg: 'bg-[#8B5CF6]',
    tests: {
      'Blood Sugar': true,
      'Blood Sugar HbA1c': false,
      'CBC / Total Count': true,
      'Lipid Profile': true,
      'Total Cholesterol, Triglycerides': false,
      'Liver Profile': true,
      'Bilirubin': false,
      'Kidney Profile': true,
      'Creatinine': false,
      'Urine Analysis': true,
      'Thyroid Profile Test': true,
      'TSH': false,
      'Vitamin D & B12': false,
      'Iron': false,
      'Calcium': true,
      'ECG': true,
      'Chest X-ray': false,
      'USG Scan Abdomen': true,
      'Cancer Screening - PSA/CA 125': false,
    },
    testCount: 10,
    highlight: true,
    tag: 'POPULAR',
  },
  {
    id: 'advance',
    name: 'Advance',
    price: 2000,
    originalPrice: 4000,
    color: 'from-[#F59E0B] to-[#D97706]',
    borderColor: 'border-[#F59E0B]',
    tagBg: 'bg-[#F59E0B]',
    tests: {
      'Blood Sugar': true,
      'Blood Sugar HbA1c': true,
      'CBC / Total Count': true,
      'Lipid Profile': true,
      'Total Cholesterol, Triglycerides': false,
      'Liver Profile': true,
      'Bilirubin': false,
      'Kidney Profile': true,
      'Creatinine': false,
      'Urine Analysis': true,
      'Thyroid Profile Test': true,
      'TSH': false,
      'Vitamin D & B12': true,
      'Iron': true,
      'Calcium': true,
      'ECG': true,
      'Chest X-ray': true,
      'USG Scan Abdomen': false,
      'Cancer Screening - PSA/CA 125': true,
    },
    testCount: 14,
    highlight: false,
    tag: 'BEST VALUE',
  },
];

const ALL_TESTS = [
  'Blood Sugar', 'Blood Sugar HbA1c', 'CBC / Total Count', 'Lipid Profile',
  'Total Cholesterol, Triglycerides', 'Liver Profile', 'Bilirubin', 'Kidney Profile',
  'Creatinine', 'Urine Analysis', 'Thyroid Profile Test', 'TSH',
  'Vitamin D & B12', 'Iron', 'Calcium', 'ECG', 'Chest X-ray',
  'USG Scan Abdomen', 'Cancer Screening - PSA/CA 125',
];

const MasterCheckup = () => {
  const [expandedPkg, setExpandedPkg] = useState(null);
  const [showComparison, setShowComparison] = useState(false);

  const bookViaWhatsApp = (pkg) => {
    const msg = `Hi, I would like to book the *${pkg.name} Master Health Checkup* package (Rs.${pkg.price}).%0A%0APlease share the appointment details.`;
    window.open(`https://wa.me/916380608090?text=${msg}`, '_blank');
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Hero */}
      <section className="relative py-16 sm:py-20 px-4" style={{ background: 'linear-gradient(135deg, #0A2540 0%, #1E3A5F 50%, #0EA5E9 100%)' }}>
        <div className="max-w-6xl mx-auto text-center relative z-10">
          <div className="inline-block bg-white/10 backdrop-blur-sm rounded-full px-6 py-2 mb-6">
            <span className="text-white font-semibold text-sm">DON'T IGNORE YOUR BODY NOTIFICATIONS</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="checkup-heading">
            Master Health Checkup
          </h1>
          <p className="text-lg sm:text-xl text-white/85 max-w-3xl mx-auto mb-6">
            Comprehensive health screening packages starting at just Rs.500. Early detection saves lives.
          </p>
          <div className="flex items-center justify-center gap-6 text-white/70 text-sm flex-wrap">
            <span className="flex items-center gap-1"><Shield className="h-4 w-4" /> NABL Accredited Lab</span>
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" /> Reports in 24 Hours</span>
            <span className="flex items-center gap-1"><Home className="h-4 w-4" /> Home Collection Available</span>
          </div>
        </div>
      </section>

      {/* Uploaded Image Banner */}
      <section className="py-6 px-4 bg-white">
        <div className="max-w-4xl mx-auto">
          <img
            src="https://customer-assets.emergentagent.com/job_amc-care/artifacts/ejah2oiy_1000572983.jpg"
            alt="AMC Master Health Checkup Packages - Full Comparison"
            className="w-full rounded-2xl shadow-xl"
            data-testid="checkup-comparison-image"
          />
        </div>
      </section>

      {/* Package Cards */}
      <section className="py-12 px-4" data-testid="packages-section">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0A2540] mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Choose Your Package
            </h2>
            <p className="text-[#475569] text-base sm:text-lg">Book via WhatsApp in seconds. Home collection available.</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PACKAGES.map(pkg => {
              const isExpanded = expandedPkg === pkg.id;
              const includedTests = ALL_TESTS.filter(t => pkg.tests[t]);
              const discount = Math.round(((pkg.originalPrice - pkg.price) / pkg.originalPrice) * 100);
              return (
                <div
                  key={pkg.id}
                  className={`relative bg-white rounded-3xl shadow-lg overflow-hidden transition-all hover:shadow-xl ${pkg.highlight ? 'ring-2 ring-[#8B5CF6] scale-[1.02]' : ''}`}
                  data-testid={`package-${pkg.id}`}
                >
                  {pkg.tag && (
                    <div className={`absolute top-0 right-0 ${pkg.tagBg} text-white text-xs font-bold px-4 py-1.5 rounded-bl-2xl z-10`}>
                      {pkg.tag}
                    </div>
                  )}

                  {/* Header */}
                  <div className={`bg-gradient-to-r ${pkg.color} px-6 py-6 text-white`}>
                    <h3 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>{pkg.name}</h3>
                    <p className="text-white/75 text-sm">{pkg.testCount} Tests Included</p>
                    <div className="mt-3 flex items-end gap-2">
                      <span className="text-4xl font-bold">Rs.{pkg.price.toLocaleString()}</span>
                      <span className="text-white/60 line-through text-lg mb-1">Rs.{pkg.originalPrice.toLocaleString()}</span>
                    </div>
                    <div className="mt-1 inline-block bg-white/20 rounded-full px-3 py-0.5 text-xs font-bold">
                      SAVE {discount}%
                    </div>
                  </div>

                  {/* Tests List */}
                  <div className="px-6 py-5">
                    <div className="space-y-2">
                      {(isExpanded ? ALL_TESTS : includedTests.slice(0, 6)).map(test => {
                        const included = pkg.tests[test];
                        return (
                          <div key={test} className={`flex items-center gap-2 text-sm ${included ? '' : 'opacity-40'}`}>
                            {included ? (
                              <div className="w-5 h-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                                <Check className="h-3 w-3 text-green-600" />
                              </div>
                            ) : (
                              <div className="w-5 h-5 rounded-full bg-red-50 flex items-center justify-center flex-shrink-0">
                                <X className="h-3 w-3 text-red-300" />
                              </div>
                            )}
                            <span className={`${included ? 'text-[#0A2540]' : 'text-[#94A3B8] line-through'}`}>{test}</span>
                          </div>
                        );
                      })}
                    </div>
                    {!isExpanded && includedTests.length > 6 && (
                      <button
                        onClick={() => setExpandedPkg(pkg.id)}
                        className="text-xs text-[#0EA5E9] font-medium mt-2 hover:underline"
                        data-testid={`expand-${pkg.id}`}
                      >
                        +{includedTests.length - 6} more tests
                      </button>
                    )}
                    {isExpanded && (
                      <button
                        onClick={() => setExpandedPkg(null)}
                        className="text-xs text-[#94A3B8] font-medium mt-2 hover:underline"
                      >
                        Show less
                      </button>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="px-6 pb-6">
                    <button
                      onClick={() => bookViaWhatsApp(pkg)}
                      className={`w-full bg-gradient-to-r ${pkg.color} text-white rounded-full py-3.5 font-bold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity shadow-md hover:shadow-lg`}
                      data-testid={`book-${pkg.id}`}
                    >
                      <MessageCircle className="h-4 w-4" />
                      Book via WhatsApp
                    </button>
                    <p className="text-center text-xs text-[#94A3B8] mt-2">Instant confirmation</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Full Comparison Table */}
      <section className="py-12 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <button
              onClick={() => setShowComparison(!showComparison)}
              className="text-[#0A2540] font-semibold text-lg hover:underline flex items-center gap-2 mx-auto"
              data-testid="toggle-comparison"
            >
              {showComparison ? 'Hide' : 'View'} Full Comparison Table
              <ArrowRight className={`h-4 w-4 transition-transform ${showComparison ? 'rotate-90' : ''}`} />
            </button>
          </div>

          {showComparison && (
            <div className="overflow-x-auto rounded-2xl shadow-lg border border-[#E2E8F0]" data-testid="comparison-table">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0A2540] text-white">
                    <th className="px-4 py-3 text-left font-semibold">Test</th>
                    {PACKAGES.map(p => (
                      <th key={p.id} className="px-4 py-3 text-center font-semibold whitespace-nowrap">{p.name}<br /><span className="text-xs font-normal opacity-75">Rs.{p.price}</span></th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {ALL_TESTS.map((test, idx) => (
                    <tr key={test} className={idx % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}>
                      <td className="px-4 py-2.5 font-medium text-[#0A2540] whitespace-nowrap">{test}</td>
                      {PACKAGES.map(p => (
                        <td key={p.id} className="px-4 py-2.5 text-center">
                          {p.tests[test] ? (
                            <Check className="h-5 w-5 text-green-500 mx-auto" />
                          ) : (
                            <X className="h-4 w-4 text-[#CBD5E1] mx-auto" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                  <tr className="bg-[#0A2540] text-white font-bold">
                    <td className="px-4 py-3">Price</td>
                    {PACKAGES.map(p => (
                      <td key={p.id} className="px-4 py-3 text-center">
                        <span className="text-lg">Rs.{p.price}</span>
                        <br />
                        <span className="text-xs line-through opacity-60">Rs.{p.originalPrice}</span>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}
        </div>
      </section>

      {/* Why Book */}
      <section className="py-12 px-4 bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Shield className="h-7 w-7 text-green-600" />
              </div>
              <h3 className="font-bold text-[#0A2540] mb-1">NABL Accredited</h3>
              <p className="text-sm text-[#475569]">Highest quality standards for accurate results</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="w-14 h-14 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Home className="h-7 w-7 text-blue-600" />
              </div>
              <h3 className="font-bold text-[#0A2540] mb-1">Home Sample Collection</h3>
              <p className="text-sm text-[#475569]">Convenient doorstep collection at no extra cost</p>
            </div>
            <div className="bg-white rounded-2xl p-6 text-center shadow-sm">
              <div className="w-14 h-14 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Clock className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="font-bold text-[#0A2540] mb-1">Reports in 24 Hours</h3>
              <p className="text-sm text-[#475569]">Quick turnaround with digital delivery</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-12 px-4 bg-gradient-to-r from-[#0A2540] to-[#0EA5E9]">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Book Your Health Checkup Today
          </h2>
          <p className="text-white/80 mb-6">Call us or book instantly via WhatsApp</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="https://wa.me/916380608090?text=Hi%2C%20I%20would%20like%20to%20book%20a%20Master%20Health%20Checkup.%20Please%20share%20the%20available%20packages." target="_blank" rel="noopener noreferrer">
              <Button className="bg-[#25D366] hover:bg-[#1da851] text-white rounded-full px-8 py-3 font-semibold text-lg shadow-lg" data-testid="whatsapp-cta">
                <MessageCircle className="mr-2 h-5 w-5" /> Book via WhatsApp
              </Button>
            </a>
            <a href="tel:6380608090">
              <Button variant="outline" className="border-2 border-white text-white hover:bg-white/10 rounded-full px-8 py-3 font-semibold text-lg">
                Call: 6380608090
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MasterCheckup;
